import { USER_ROLES } from '~shared/constants/account-info.constant';
import { environment } from '~env/environment';
import { MarketDetectionUtil } from '~shared/utils/market-detection.util';
import { ACCOUNT_MENU_GA_EVENT_LABEL, ACCOUNT_SIDE_BAR_MENU_QA_LOCATOR } from '../constants/account.constant';
export namespace AccountSideBarUtils {
  export const SUPPORT_ITEM = {
    title: 'PAGES.ACCOUNT.SUPPORT.TITLE',
    iconName: 'help',
    qaLocator: 'support',
    gaEventLabel: ACCOUNT_MENU_GA_EVENT_LABEL.support,
  };

  export const LOGOUT_ITEM = {
    title: 'PAGES.ACCOUNT.MENU.LOGOUT.TITLE',
    iconName: 'logout',
    qaLocator: 'logout',
    gaEventLabel: ACCOUNT_MENU_GA_EVENT_LABEL.logout,
  };

  function getProfileItem(baseUrl: string, context: string, newSideBar: boolean) {
    return {
      [USER_ROLES.entrepreneur]: {
        title: 'PAGES.ACCOUNT.MENU.BUSINESS_PROFILE.TITLE',
        link: `${baseUrl}/business/account/${context}/profile`,
        iconName: 'business',
        gaEventLabel: ACCOUNT_MENU_GA_EVENT_LABEL.businessProfile,
        qaLocator: ACCOUNT_SIDE_BAR_MENU_QA_LOCATOR.businessProfile
      },
      [USER_ROLES.consumer]: {
        title: 'PAGES.ACCOUNT.MENU.PERSONAL_PROFILE.TITLE',
        link: `${baseUrl}/personal/profile?context=${context}`,
        iconName: newSideBar ? 'personal-profile' : 'business',
        gaEventLabel: ACCOUNT_MENU_GA_EVENT_LABEL.personalProfile,
        qaLocator: newSideBar ? ACCOUNT_SIDE_BAR_MENU_QA_LOCATOR.personalProfile :
          ACCOUNT_SIDE_BAR_MENU_QA_LOCATOR.businessProfile,
      },
    };
  }

  function getAddressBookItem(baseUrl: string, context: string, newSideBar: boolean) {
    return {
      [USER_ROLES.entrepreneur]: {
        title: 'PAGES.ACCOUNT.MENU.BUSINESS_ADDRESSES.TITLE',
        link: `${baseUrl}/business/account/${context}/addresses`,
        gaEventLabel: ACCOUNT_MENU_GA_EVENT_LABEL.businessAddresses,
        qaLocator: ACCOUNT_SIDE_BAR_MENU_QA_LOCATOR.businessAddresses,
      },
      [USER_ROLES.consumer]: {
        title: 'PAGES.ACCOUNT.MENU.PERSONAL_ADDRESSES.TITLE',
        link: `${baseUrl}/personal/addresses?context=${context}`,
        gaEventLabel: ACCOUNT_MENU_GA_EVENT_LABEL.personalAddresses,
        qaLocator: newSideBar ? ACCOUNT_SIDE_BAR_MENU_QA_LOCATOR.personalAddresses :
          ACCOUNT_SIDE_BAR_MENU_QA_LOCATOR.businessAddresses,
      },
    };
  }

  function getProfile(role: string, profileItem: any) {
    return {
      title: profileItem[role].title,
      link: profileItem[role].link,
      iconName: profileItem[role].iconName,
      qaLocator: profileItem[role].qaLocator,
      gaEventLabel: profileItem[role].gaEventLabel
    };
  }

  function getAddressBook(role: string, addressBookItem: any) {
    return {
      title: addressBookItem[role].title,
      link: addressBookItem[role].link,
      iconName: 'location',
      qaLocator: addressBookItem[role].qaLocator,
      gaEventLabel: addressBookItem[role].gaEventLabel,
    };
  }

  function getPaymentLink(url: string, isPaymentLinkEnabled: boolean) {
    return {
      title: 'PAGES.ACCOUNT.MENU.PAYMENT_METHODS.TITLE',
      link: `${url}/account/stored-payment-methods`,
      iconName: 'card-2',
      qaLocator: 'store-payment-methods',
      disabled: !isPaymentLinkEnabled,
      gaEventLabel: ACCOUNT_MENU_GA_EVENT_LABEL.storedPaymentMethod,
    };
  }

  export function getMenuItems(role: string, context: string, isTier3 = false, newSideBar?: boolean, isPaymentLinkEnabled?: boolean) { // NOSONAR
    const isConsumer = USER_ROLES.consumer === role;
    const metroAppsBaseUrl = environment.myMetroBaseUrl();
    const countryAppsBaseUrl = environment.myMetroCountryBaseUrl();
    const profileItem = getProfileItem(metroAppsBaseUrl, context, newSideBar);
    const addressBookItem = getAddressBookItem(metroAppsBaseUrl, context, newSideBar);
    const buyerBaseUrl = environment.appBaseUrl();

    return {
      profile: getProfile(role, profileItem),
      addressBook: getAddressBook(role, addressBookItem),
      staffAndPermissions: {
        title: 'PAGES.ACCOUNT.MENU.STAFF_AND_PERMISSIONS.TITLE',
        link: `${metroAppsBaseUrl}/business/account/${context}/staff-and-permissions`,
        iconName: 'group',
        qaLocator: 'staff-permissions',
        disabled: isConsumer,
        gaEventLabel: ACCOUNT_MENU_GA_EVENT_LABEL.staffAndPermissions,
      },
      metroMail: {
        title: 'PAGES.ACCOUNT.MENU.METRO_MAIL.TITLE',
        link: `${metroAppsBaseUrl}/business/account/${context}/mail`,
        iconName: 'broshures',
        qaLocator: 'mail',
        disabled: isConsumer || !MarketDetectionUtil.isSpanishMarket(),
        gaEventLabel: ACCOUNT_MENU_GA_EVENT_LABEL.metroMail,
      },
      promotionalFlyers: {
        title: 'PAGES.ACCOUNT.MENU.PROMOTIONAL_FLYERS.TITLE',
        link: `${metroAppsBaseUrl}/business/account/${context}/leaflet`,
        iconName: 'broshures',
        qaLocator: 'promotional-flyers',
        disabled: !isTier3 || !MarketDetectionUtil.isItalianMarket(),
        gaEventLabel: ACCOUNT_MENU_GA_EVENT_LABEL.promotionalFlyers,
      },
      digitalPurchase: {
        title: 'PAGES.ACCOUNT.MENU.DIGITAL_PURCHASE.TITLE',
        link: `${countryAppsBaseUrl}/invite_me`,
        iconName: 'group',
        qaLocator: 'digital-purchase',
        disabled: !isTier3 || !MarketDetectionUtil.isGermanMarket(),
        gaEventLabel: ACCOUNT_MENU_GA_EVENT_LABEL.digitalPurchaseAuthorization,
      },
      purchases: {
        title: 'PAGES.ACCOUNT.MENU.PURCHASES.TITLE',
        linkTitle: 'PAGES.ACCOUNT.MENU.PURCHASES_ONLINE_SHOP.TITLE',
        link: (newSideBar && isConsumer) ? `${buyerBaseUrl}/account/orders-history?context=personal`
          : `${buyerBaseUrl}/account/orders-history`,
        iconName: 'shopping-cart',
        qaLocator: 'purchases',
        gaEventLabel: isConsumer ? ACCOUNT_MENU_GA_EVENT_LABEL.personalOrderHistory :
          ACCOUNT_MENU_GA_EVENT_LABEL.businessOrderHistory,
        menuItem: 'purchases',
      },
      paymentLink: getPaymentLink(buyerBaseUrl, (isConsumer && isPaymentLinkEnabled)),
      newslettersAndPromotions: {
        title: 'PAGES.ACCOUNT.MENU.NEWSLETTERS_PROMOTIONS.TITLE',
        link: `${metroAppsBaseUrl}/personal/promo-settings?context=${context}`,
        iconName: 'promotion',
        qaLocator: 'newsletters-promotion',
        disabled: (newSideBar && !isConsumer),
        gaEventLabel: ACCOUNT_MENU_GA_EVENT_LABEL.newslettersAndPromotions,
      },
      loginAndSecurity: {
        title: 'PAGES.ACCOUNT.MENU.LOGIN_SECURITY.TITLE',
        link: `${metroAppsBaseUrl}/personal/login-security?context=${context}`,
        iconName: 'security',
        qaLocator: 'login-security',
        disabled: (newSideBar && !isConsumer),
        gaEventLabel: ACCOUNT_MENU_GA_EVENT_LABEL.loginAndSecurity,
      }
    };
  }
}
